// Automatic layout: ELK (in its own web worker) for layered, organic and grouped layouts, and the
// plain arrangements of layouts.ts for the grid and radial ones.
import ELK, { type ElkNode } from 'elkjs/lib/elk-api.js';
import elkWorkerUrl from 'elkjs/lib/elk-worker.min.js?url';
import { assignGroups, gridLayout, radialLayout, removeOverlaps, spacingScale, type FlowKind, type LayoutEdge, type LayoutKind, type LayoutNode, type Positions, type Spacing, type SpacingScale } from './layouts';

export type { LayoutEdge, LayoutNode, Positions } from './layouts';

let elk: InstanceType<typeof ELK> | null = null;

function engine(): InstanceType<typeof ELK> {
  elk ??= new ELK({ workerUrl: elkWorkerUrl });
  return elk;
}

const DIRECTION: Record<FlowKind, string> = { right: 'RIGHT', left: 'LEFT', down: 'DOWN', up: 'UP' };

const px = (n: number) => String(Math.round(n));

/**
 * Above this many cards, keeping the model's order makes ELK take tens of seconds (300 cards of a
 * code map: 30 s), and so do the many lines between the same two cards: large diagrams go without
 * both. Smaller ones keep their arrangement as it was.
 */
const LARGE = 120;

const layered = (kind: FlowKind, s: SpacingScale, cards: number) => ({
  'elk.algorithm': 'layered',
  'elk.direction': DIRECTION[kind],
  'elk.spacing.nodeNode': px(40 * s.nodes),
  'elk.layered.spacing.nodeNodeBetweenLayers': px((kind === 'right' || kind === 'left' ? 110 : 80) * s.layers),
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.considerModelOrder.strategy': cards > LARGE ? 'NONE' : 'NODES_AND_EDGES',
  'elk.separateConnectedComponents': 'true',
  'elk.spacing.componentComponent': px(80 * s.nodes),
  'elk.aspectRatio': '1.8',
});

function elkEdges(nodes: LayoutNode[], edges: LayoutEdge[]) {
  const ids = new Set(nodes.map((n) => n.id));
  const kept = edges.filter((e) => ids.has(e.from) && ids.has(e.to) && e.from !== e.to);
  // One line per pair of cards on large diagrams: placing the cards needs no more.
  const seen = new Set<string>();
  const shown =
    nodes.length > LARGE
      ? kept.filter((e) => {
          const pair = `${e.from}|${e.to}`;
          if (seen.has(pair)) return false;
          seen.add(pair);
          return true;
        })
      : kept;
  return shown.map((e) => ({ id: e.id, sources: [e.from], targets: [e.to] }));
}

function positionsOf(result: ElkNode, out: Positions = {}, dx = 0, dy = 0): Positions {
  for (const c of result.children ?? []) {
    const x = dx + (c.x ?? 0);
    const y = dy + (c.y ?? 0);
    // Group frames hold their cards; only the cards get positions.
    if (c.id.startsWith('group:')) positionsOf(c, out, x, y);
    else out[c.id] = { x, y };
  }
  return out;
}

export async function autoLayout(nodes: LayoutNode[], edges: LayoutEdge[], kind: LayoutKind = 'right', spacing: Spacing = 'normal'): Promise<Positions> {
  const s = spacingScale(spacing, nodes.length);
  if (kind === 'grid') return gridLayout(nodes, s.nodes);
  if (kind === 'radial') return radialLayout(nodes, edges, undefined, s.layers);
  if (kind === 'organic') {
    const result = await engine().layout({
      id: 'root',
      layoutOptions: {
        'elk.algorithm': 'stress',
        'elk.stress.desiredEdgeLength': px(320 * s.layers),
        'elk.separateConnectedComponents': 'true',
        'elk.spacing.componentComponent': px(120 * s.nodes),
      },
      children: nodes.map((n) => ({ id: n.id, width: n.width, height: n.height })),
      edges: elkEdges(nodes, edges),
    });
    return removeOverlaps(nodes, positionsOf(result), 32 * s.nodes);
  }
  if (kind === 'namespace' || kind === 'schema') {
    const groups = assignGroups(nodes, edges, kind);
    const names = [...new Set(groups.values())].sort();
    const result = await engine().layout({
      id: 'root',
      layoutOptions: { ...layered('right', s, nodes.length), 'elk.hierarchyHandling': 'INCLUDE_CHILDREN', 'elk.spacing.nodeNode': px(60 * s.nodes) },
      children: names.map((g) => ({
        id: `group:${g}`,
        layoutOptions: { 'elk.padding': '[top=56,left=28,bottom=28,right=28]' },
        children: nodes.filter((n) => groups.get(n.id) === g).map((n) => ({ id: n.id, width: n.width, height: n.height })),
      })),
      edges: elkEdges(nodes, edges),
    });
    return positionsOf(result);
  }
  const result = await engine().layout({
    id: 'root',
    layoutOptions: layered(kind, s, nodes.length),
    children: nodes.map((n) => ({ id: n.id, width: n.width, height: n.height })),
    edges: elkEdges(nodes, edges),
  });
  return positionsOf(result);
}

/** A layout for loosely connected graphs (the notes graph): related nodes close together. */
export async function stressLayout(nodes: LayoutNode[], edges: LayoutEdge[]): Promise<Positions> {
  const result = await engine().layout({
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'stress',
      'elk.stress.desiredEdgeLength': '150',
      'elk.spacing.nodeNode': '30',
      'elk.separateConnectedComponents': 'true',
      'elk.spacing.componentComponent': '60',
    },
    children: nodes.map((n) => ({ id: n.id, width: n.width, height: n.height })),
    edges: elkEdges(nodes, edges),
  });
  return positionsOf(result);
}
